import { useState } from 'react';
import { ArrowDown, ArrowUp, Eye, Plus, Trash2 } from 'lucide-react';
import { Select } from '../ui/Select';
import { useI18n } from '../../contexts/I18nContext';
import { useCreateVaultEntry, useUpdateVaultEntry } from '../../hooks/useVault';
import {
  revealVaultEntry,
  VAULT_TYPES,
  type VaultEntry,
  type VaultFieldInput,
  type VaultType,
} from '../../lib/vaultApi';

// ---------------------------------------------------------------------------
// Create / edit a vault entry.
//
// Fields are entirely free-form: picking a type only pre-fills the labels teams
// usually need for that kind of credential, and every one of them can be
// renamed, reordered or removed.
//
// Editing never receives the stored secrets — they show as an empty input with
// a "(unchanged)" placeholder and are only rewritten if the user types
// something. "Load the real values" fetches them through the reveal endpoint,
// which requires canRevealVault and writes an audit row.
// ---------------------------------------------------------------------------

const FIELD_PRESETS: Partial<Record<VaultType, VaultFieldInput[]>> = {
  SERVER: [
    { label: 'Host', value: '', secret: false },
    { label: 'Port', value: '', secret: false },
    { label: 'Username', value: '', secret: false },
    { label: 'Parolă', value: '', secret: true },
  ],
  GITHUB: [
    { label: 'Username', value: '', secret: false },
    { label: 'Token', value: '', secret: true },
    { label: 'Organizație', value: '', secret: false },
  ],
  GITLAB: [
    { label: 'Username', value: '', secret: false },
    { label: 'Token', value: '', secret: true },
  ],
  AZURE: [
    { label: 'Tenant ID', value: '', secret: false },
    { label: 'Subscription ID', value: '', secret: false },
    { label: 'Client ID', value: '', secret: false },
    { label: 'Client Secret', value: '', secret: true },
  ],
  AWS: [
    { label: 'Access Key ID', value: '', secret: false },
    { label: 'Secret Access Key', value: '', secret: true },
    { label: 'Region', value: '', secret: false },
  ],
  DATABASE: [
    { label: 'Host', value: '', secret: false },
    { label: 'Port', value: '', secret: false },
    { label: 'Database', value: '', secret: false },
    { label: 'Username', value: '', secret: false },
    { label: 'Parolă', value: '', secret: true },
  ],
  EMAIL: [
    { label: 'Adresă', value: '', secret: false },
    { label: 'Parolă', value: '', secret: true },
    { label: 'IMAP', value: '', secret: false },
    { label: 'SMTP', value: '', secret: false },
  ],
  FTP: [
    { label: 'Host', value: '', secret: false },
    { label: 'Username', value: '', secret: false },
    { label: 'Parolă', value: '', secret: true },
  ],
  API: [
    { label: 'API Key', value: '', secret: true },
    { label: 'Endpoint', value: '', secret: false },
  ],
  HOSTING: [
    { label: 'Panou', value: '', secret: false },
    { label: 'Username', value: '', secret: false },
    { label: 'Parolă', value: '', secret: true },
  ],
  DOMAIN: [
    { label: 'Registrar', value: '', secret: false },
    { label: 'Username', value: '', secret: false },
    { label: 'Parolă', value: '', secret: true },
  ],
  ACCOUNT: [
    { label: 'Username', value: '', secret: false },
    { label: 'Parolă', value: '', secret: true },
  ],
};

interface Props {
  projectId: string;
  /** Undefined = create mode. */
  entry?: VaultEntry;
  canReveal: boolean;
  onClose: () => void;
}

export function VaultEntryModal({ projectId, entry, canReveal, onClose }: Props) {
  const { t } = useI18n();
  const createMutation = useCreateVaultEntry(projectId);
  const updateMutation = useUpdateVaultEntry(projectId);

  const [title, setTitle] = useState(entry?.title ?? '');
  const [type, setType] = useState<VaultType>(
    (VAULT_TYPES as readonly string[]).includes(entry?.type ?? '')
      ? (entry!.type as VaultType)
      : 'SERVER',
  );
  const [url, setUrl] = useState(entry?.url ?? '');
  const [notes, setNotes] = useState(entry?.notes ?? '');
  const [fields, setFields] = useState<VaultFieldInput[]>(
    entry
      ? entry.fields.map((f) => ({ label: f.label, value: f.value ?? '', secret: f.secret }))
      : (FIELD_PRESETS.SERVER ?? []),
  );
  const [secretsLoaded, setSecretsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function handleTypeChange(next: string) {
    const nextType = next as VaultType;
    setType(nextType);
    // Only pre-fill when the user hasn't put anything in yet, so switching type
    // by accident can't wipe half-entered credentials.
    const untouched = fields.every((f) => f.value === '');
    if (untouched && FIELD_PRESETS[nextType]) {
      setFields(FIELD_PRESETS[nextType]!.map((f) => ({ ...f })));
    }
  }

  function patchField(index: number, patch: Partial<VaultFieldInput>) {
    setFields((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  }

  function moveField(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= fields.length) return;
    setFields((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  async function loadSecrets() {
    if (!entry) return;
    setError(null);
    try {
      const revealed = await revealVaultEntry(projectId, entry.id);
      const byLabel = new Map(revealed.map((f) => [f.label, f.value]));
      setFields((prev) =>
        prev.map((f) =>
          f.secret && byLabel.has(f.label) ? { ...f, value: byLabel.get(f.label)! } : f,
        ),
      );
      setSecretsLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('vault.revealFailed'));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError(t('vault.modal.titleRequired'));
      return;
    }
    if (fields.some((f) => !f.label.trim())) {
      setError(t('vault.modal.labelRequired'));
      return;
    }
    const input = {
      title: trimmedTitle,
      type,
      url: url.trim() || null,
      notes: notes.trim() || null,
      fields: fields.map((f) => ({ label: f.label.trim(), value: f.value, secret: f.secret })),
    };
    setError(null);
    setSaving(true);
    try {
      if (entry) {
        await updateMutation.mutateAsync({ entryId: entry.id, input });
      } else {
        await createMutation.mutateAsync(input);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('vault.modal.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ui-modal-backdrop" role="dialog" aria-modal="true">
      <form
        onSubmit={handleSubmit}
        className="ui-modal-panel max-h-[90vh] w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-gray-900">
          {entry ? t('vault.modal.editTitle') : t('vault.modal.createTitle')}
        </h2>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="ui-field-label" htmlFor="vault-title">
              {t('vault.modal.title')}
            </label>
            <input
              id="vault-title"
              className="ui-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('vault.modal.titlePlaceholder')}
              autoFocus
            />
          </div>
          <Select
            label={t('vault.modal.type')}
            value={type}
            onChange={handleTypeChange}
            options={VAULT_TYPES.map((value) => ({ value, label: t(`vault.type.${value}`) }))}
          />
          <div className="sm:col-span-2">
            <label className="ui-field-label" htmlFor="vault-url">
              {t('vault.modal.url')}
            </label>
            <input
              id="vault-url"
              className="ui-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="ui-field-label" htmlFor="vault-notes">
              {t('vault.modal.notes')}
            </label>
            <textarea
              id="vault-notes"
              className="ui-textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between">
            <span className="ui-field-label mb-0">{t('vault.modal.fields')}</span>
            {entry && canReveal && !secretsLoaded && (
              <button type="button" className="ui-btn-secondary" onClick={loadSecrets}>
                <Eye size={14} /> {t('vault.modal.loadSecrets')}
              </button>
            )}
          </div>

          {fields.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500">{t('vault.modal.noFields')}</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {fields.map((field, index) => (
                <li key={index} className="flex flex-wrap items-center gap-2">
                  <input
                    className="ui-input w-40 flex-none"
                    value={field.label}
                    onChange={(e) => patchField(index, { label: e.target.value })}
                    placeholder={t('vault.modal.fieldLabel')}
                    aria-label={t('vault.modal.fieldLabel')}
                  />
                  <input
                    className="ui-input min-w-0 flex-1"
                    type={field.secret && !secretsLoaded ? 'password' : 'text'}
                    value={field.value}
                    onChange={(e) => patchField(index, { value: e.target.value })}
                    placeholder={
                      entry && field.secret && !secretsLoaded
                        ? t('vault.modal.unchanged')
                        : t('vault.modal.fieldValue')
                    }
                    aria-label={t('vault.modal.fieldValue')}
                  />
                  <label className="flex flex-none items-center gap-1.5 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      className="ui-checkbox"
                      checked={field.secret}
                      onChange={(e) => patchField(index, { secret: e.target.checked })}
                    />
                    {t('vault.modal.secret')}
                  </label>
                  <button
                    type="button"
                    onClick={() => moveField(index, -1)}
                    disabled={index === 0}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
                    aria-label={t('vault.modal.moveUp')}
                  >
                    <ArrowUp size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveField(index, 1)}
                    disabled={index === fields.length - 1}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
                    aria-label={t('vault.modal.moveDown')}
                  >
                    <ArrowDown size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setFields((prev) => prev.filter((_, i) => i !== index))}
                    className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    aria-label={t('vault.modal.removeField')}
                  >
                    <Trash2 size={15} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            className="ui-btn-secondary mt-3"
            onClick={() => setFields((prev) => [...prev, { label: '', value: '', secret: false }])}
          >
            <Plus size={14} /> {t('vault.modal.addField')}
          </button>
        </div>

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="ui-btn-secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="ui-btn-primary" disabled={saving}>
            {t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
